import { createContext, useContext, useEffect, useState } from 'react';
import {
    ApiHandler,
    LoginResponse,
    mapSignedUserToLocalUser,
} from '@givelify/api';
import { emailRegex } from '@givelify/givelify-ui';
import {
    Logger,
    onLogin,
    useApiRequest,
    useTrackingContext,
} from '@givelify/utils';
import { yupResolver } from '@hookform/resolvers/yup';
import { SignInRequest } from 'api/requests/auth';
import { FormProvider, useForm } from 'react-hook-form';
import { useAppDispatch } from 'store';
import { getUserEmailLocalStorage } from 'store/user/accessTokenInLocalStorage';
import { setAccessToken, setUser } from 'store/user/actions';
import {
    LOGIN_PAGE_CLICK_SIGN_IN,
    LOGIN_PAGE_VIEW_NOT_VERIFIED,
} from 'utils/clevertapEvents';
import { useRecaptcha } from 'utils/useRecaptcha';
import { webConfig } from 'webConfig';
import { object, string } from 'yup';
import { useLoginFormText } from './LoginForm/useLoginFormText';

type LoginContextType = {
    submitForm: React.FormEventHandler<HTMLFormElement>;
    formError: string;
    isValid: boolean;
    isLoading: boolean;
    showNotVerified: boolean;
};

const LoginContext = createContext<LoginContextType>({
    submitForm: () => undefined,
    formError: '',
    isValid: false,
    isLoading: false,
    showNotVerified: false,
});

export const useLoginContext = () => useContext(LoginContext);

export const LoginContextProvider: React.FCC = ({ children }) => {
    const loginFormText = useLoginFormText();
    const { trackEvent } = useTrackingContext();
    const { getRecaptchaToken } = useRecaptcha('signin');
    const dispatch = useAppDispatch();

    const [formError, setFormError] = useState<string>('');
    const [showNotVerified, setShowNotVerified] = useState<boolean>(false);

    const [loginApi, makeLoginRequest] = useApiRequest<LoginResponse>();

    const form = useForm<SignInRequest>({
        mode: 'onChange',
        reValidateMode: 'onChange',
        resolver: yupResolver(
            object({
                username: string()
                    .required(loginFormText.text.requiredEmail)
                    .matches(emailRegex, loginFormText.text.invalidEmail),
                password: string().required(
                    loginFormText.text.requiredPassword,
                ),
            }),
        ),
        defaultValues: {
            username: getUserEmailLocalStorage(),
        },
    });

    const handleSubmit = async (value: SignInRequest) => {
        trackEvent(LOGIN_PAGE_CLICK_SIGN_IN);
        const token = await getRecaptchaToken();
        if (token) {
            makeLoginRequest(
                ApiHandler.instance.auth.login({
                    ...value,
                    captchaToken: token,
                }),
            );
        } else {
            setFormError(loginFormText.notRobot);
        }
    };

    useEffect(() => {
        if (loginApi.type === 'REQUEST_START') {
            setShowNotVerified(false);
        }
        if (loginApi.type === 'REQUEST_ERROR') {
            if (loginApi.error.status === 412) {
                // not verified
                setFormError(null);
                setShowNotVerified(true);
                trackEvent(LOGIN_PAGE_VIEW_NOT_VERIFIED, null, 'view');
            } else {
                setFormError(loginApi.error.message);
            }
        }
        if (loginApi.type === 'REQUEST_SUCCESS') {
            Logger.log(
                `User ${loginApi.response.user.id} logged in successfully`,
                {
                    id: loginApi.response.user.id,
                    doneeId: loginApi.response.user.doneeId,
                },
                Logger.LogLevel.INFO,
            );
            const user = mapSignedUserToLocalUser(loginApi.response.user);
            dispatch(setUser(user));
            dispatch(setAccessToken(loginApi.response.accessToken));
            onLogin(user, webConfig.appVersion);
            Logger.configureUserData(user.doneeId, user.id);
        }
    }, [loginApi, dispatch, trackEvent]);

    const submitForm = form.handleSubmit(handleSubmit);

    return (
        <LoginContext.Provider
            value={{
                formError,
                isValid: form.formState.isValid,
                submitForm,
                isLoading:
                    loginApi.type === 'REQUEST_START' ||
                    loginApi.type === 'REQUEST_SUCCESS',
                showNotVerified,
            }}
        >
            <FormProvider {...form}>{children}</FormProvider>
        </LoginContext.Provider>
    );
};
